// src/components/layout/Footer.tsx
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Container } from '@/components/ui/Container'
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react'

export function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-brand-dark text-white">
      {/* Main Footer */}
      <Container className="py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Column 1: Logo e Info */}
          <div>
            <img
              src="/images/logo_interbrau-big_white.png"
              alt={t('footer.logoAlt', 'Interbrau')}
              width={180}
              height={50}
              className="h-12 w-auto mb-6"
            />
            <p className="text-white/70 text-sm leading-relaxed">
              {t('footer.description', 'Distributore di birre speciali italiane e internazionali dal 1985. Birra per passione.')}
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.navigation', 'Navigazione')}
            </h3>
            <nav className="flex flex-col space-y-2">
              <LocalizedLink to="contacts" className="text-white/70 hover:text-brand-primary transition-colors">
                {t('nav.contacts')}
              </LocalizedLink>
              <LocalizedLink to="privacy" className="text-white/70 hover:text-brand-primary transition-colors">
                {t('nav.privacy')}
              </LocalizedLink>
              <LocalizedLink to="whistleblowing" className="text-white/70 hover:text-brand-primary transition-colors">
                {t('nav.whistleblowing', 'Whistleblowing')}
              </LocalizedLink>
              <LocalizedLink to="accessibility" className="text-white/70 hover:text-brand-primary transition-colors">
                {t('nav.accessibility', 'Accessibilità')}
              </LocalizedLink>
            </nav>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.contact', 'Contatti')}
            </h3>
            <div className="space-y-3">
              <a
                href="https://maps.google.com/?q=Viale+Delle+Industrie+32+35010+Villafranca+Padovana"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start text-white/70 hover:text-brand-primary transition-colors"
              >
                <MapPin className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  Viale Delle Industrie, 32<br />
                  35010 Villafranca Padovana (PD)
                </span>
              </a>
              <a
                href="tel:+390499099111"
                className="flex items-center text-white/70 hover:text-brand-primary transition-colors"
              >
                <Phone className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-sm">+39 049 9099111</span>
              </a>
              <a
                href="mailto:info@interbrau.it"
                className="flex items-center text-white/70 hover:text-brand-primary transition-colors"
              >
                <Mail className="w-5 h-5 mr-2 flex-shrink-0" />
                <span className="text-sm">info@interbrau.it</span>
              </a>
            </div>
          </div>

          {/* Column 4: Social & Newsletter */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              {t('footer.followUs', 'Seguici')}
            </h3>
            <div className="flex space-x-4 mb-6">
              <a
                href="https://facebook.com/interbrau"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-primary transition-colors"
                aria-label={t('aria.visitFacebook', 'Facebook')}
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/interbrau"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-primary transition-colors"
                aria-label={t('aria.visitInstagram', 'Instagram')}
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/interbrau"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-primary transition-colors"
                aria-label={t('aria.visitLinkedin', 'LinkedIn')}
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>

            <h4 className="font-medium text-sm mb-2">
              {t('footer.newsletter', 'Newsletter')}
            </h4>
            <form className="flex" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder', 'Email')}
                className="flex-1 px-4 py-2 rounded-l-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-brand-primary"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-brand-primary text-white rounded-r-md hover:bg-brand-primary/90 transition-colors"
              >
                {t('buttons.subscribe', 'Iscriviti')}
              </button>
            </form>
          </div>
        </div>
      </Container>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <Container className="py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {currentYear} Interbrau S.r.l. {t('footer.allRightsReserved', 'Tutti i diritti riservati.')}
            </p>
            <nav className="flex items-center space-x-6">
              <LocalizedLink
                to="privacy"
                className="text-white/50 hover:text-white text-sm transition-colors"
              >
                {t('footer.privacy', 'Privacy Policy')}
              </LocalizedLink>
              <span className="text-white/30">|</span>
              <a
                href="#"
                className="text-white/50 hover:text-white text-sm transition-colors"
              >
                {t('footer.cookies', 'Cookie Policy')}
              </a>
            </nav>
          </div>
        </Container>
      </div>
    </footer>
  )
}
